import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import {
  AudienceCampaign,
  AudienceContactsResponse,
  CreateAudienceCampaignRequest,
  PostmarkTemplateOption,
  StartAudienceCampaignRequest,
  UpdateAudienceCampaignSettingsRequest,
} from '../models/audience.model';

@Injectable({ providedIn: 'root' })
export class AudienceAdminService {
  #http = inject(HttpClient);
  #base = `${environment.baseUrl}/audience`;

  listContacts(params: {
    tags: string[];
    match?: 'any' | 'all';
    limit?: number;
    offset?: number;
  }): Promise<AudienceContactsResponse> {
    let httpParams = new HttpParams().set('tags', params.tags.join(','));
    if (params.match) httpParams = httpParams.set('match', params.match);
    if (params.limit != null)
      httpParams = httpParams.set('limit', String(params.limit));
    if (params.offset != null)
      httpParams = httpParams.set('offset', String(params.offset));

    return lastValueFrom(
      this.#http
        .get<AudienceContactsResponse>(`${this.#base}/contacts`, {
          params: httpParams,
        })
        .pipe(take(1)),
    );
  }

  upsertContact(
    email: string,
    tags: string[],
    name?: string,
  ): Promise<unknown> {
    return lastValueFrom(
      this.#http
        .post(`${this.#base}/contacts`, {
          email,
          tags,
          ...(name ? { name } : {}),
        })
        .pipe(take(1)),
    );
  }

  listCampaigns(): Promise<AudienceCampaign[]> {
    return lastValueFrom(
      this.#http.get<AudienceCampaign[]>(`${this.#base}/campaigns`).pipe(take(1)),
    );
  }

  listTemplates(): Promise<PostmarkTemplateOption[]> {
    return lastValueFrom(
      this.#http
        .get<PostmarkTemplateOption[]>(`${this.#base}/templates`)
        .pipe(take(1)),
    );
  }

  createCampaign(
    body: CreateAudienceCampaignRequest,
  ): Promise<AudienceCampaign> {
    return lastValueFrom(
      this.#http
        .post<AudienceCampaign>(`${this.#base}/campaigns`, body)
        .pipe(take(1)),
    );
  }

  updateCampaignSettings(
    id: string,
    body: UpdateAudienceCampaignSettingsRequest,
  ): Promise<AudienceCampaign> {
    return lastValueFrom(
      this.#http
        .post<AudienceCampaign>(`${this.#base}/campaigns/${id}/settings`, body)
        .pipe(take(1)),
    );
  }

  startCampaign(
    id: string,
    body: StartAudienceCampaignRequest = {},
  ): Promise<AudienceCampaign> {
    return lastValueFrom(
      this.#http
        .post<AudienceCampaign>(`${this.#base}/campaigns/${id}/start`, body)
        .pipe(take(1)),
    );
  }

  archiveCampaign(id: string): Promise<AudienceCampaign> {
    return lastValueFrom(
      this.#http
        .post<AudienceCampaign>(`${this.#base}/campaigns/${id}/archive`, {})
        .pipe(take(1)),
    );
  }
}
